import { resolveDisplayName } from "../utils/displayName";
import { useAppSelector } from "./hooks";
import { selectSite } from "./selectors";
import type { SiteState } from "./slices";

export interface ResolvedSiteState extends SiteState {
  resolvedDisplayName: string;
  /** Whether guests can view the gallery at all. Parent of single-image download. Defaults true. */
  allowGallery: boolean;
  /** Whether guests may download an individual photo/video. Gated by the gallery. Defaults true. */
  allowSingleImageDownload: boolean;
  /** Whether the guest zip-download page is enabled. Independent of the gallery. Defaults false. */
  allowZipDownload: boolean;
  /** Whether the wedding-day timeline page is enabled. Independent of the gallery. Defaults false. */
  allowTimeline: boolean;
  /** Whether the food & drink menu page is enabled. Independent of the gallery. Defaults false. */
  allowMenu: boolean;
  /** Whether the DJ song-requests page is enabled. Independent of the gallery. Defaults false. */
  allowDjRequests: boolean;
}

/**
 * Returns the resolved site. AppBootstrap guarantees this is non-null
 * before rendering the main App, so consumers can rely on it.
 */
export function useSite(): ResolvedSiteState {
  const site = useAppSelector(selectSite);
  if (!site) {
    // Defensive — should never happen because AppBootstrap blocks rendering
    // until setSite has been dispatched.
    throw new Error("useSite() called before the site was resolved.");
  }
  const permissions = site.config.permissions;
  // Gallery is the parent of the single-image download: when it is off, that
  // download is forced off too, regardless of its stored value. The zip
  // download is independent and resolves purely from its own flag.
  const allowGallery = permissions?.allowGallery !== false;
  return {
    ...site,
    resolvedDisplayName: resolveDisplayName(site.displayName, site.name),
    allowGallery,
    allowSingleImageDownload:
      allowGallery && permissions?.allowSingleImageDownload !== false,
    allowZipDownload: permissions?.allowZipDownload === true,
    allowTimeline: permissions?.allowTimeline === true,
    allowMenu: permissions?.allowMenu === true,
    allowDjRequests: permissions?.allowDjRequests === true,
  };
}

